package gr.tech.GRAttestation.sections.forms

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.GRAttestation.components.inputs.GRDollarInput
import gr.tech.GRAttestation.components.inputs.GRStringInput
import gr.tech.GRAttestation.styles.GeneralFormErrorTextStyle
import gr.tech.GRAttestation.util.parseCurrencyInput
import gr.tech.models.skus.AMOUNT_PER_RECEIPT_DEFAULT
import gr.tech.models.skus.SkuDTO
import gr.tech.util.validateCurrencyInput
import gr.tech.util.validateInput
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Composable
fun SKUForm(
    skuInformation: SkuDTO,
    formError: String,
    onSubmit: (SkuDTO) -> Unit,
) {
    var form by remember { mutableStateOf(skuInformation) }

    var skuNameError by remember { mutableStateOf("") }
    var skuDescriptionError by remember { mutableStateOf("") }
    var skuUnitPriceError by remember { mutableStateOf("") }
    var skuAmountPerReceiptError by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        skuNameError = form.sku.validateInput(minLength = 3, maxLength = 128)
        skuDescriptionError = form.description.validateInput(minLength = 3)
        skuUnitPriceError = form.unitPrice.validateInput(min = 1)
        skuAmountPerReceiptError = form.amountPerReceipt.validateInput(min = 0)
    }

    Column(
        modifier = Modifier.margin(top = 8.px),
        horizontalAlignment = Alignment.Start
    ) {
        GRStringInput(
            direction = FlexDirection.Column,
            inputId = "SKUString",
            labelText = "SKU / Model #",
            value = form.sku,
            hasError = skuNameError.isNotBlank(),
            labelError = skuNameError,
            labelModifier = Modifier
                .whiteSpace(WhiteSpace.NoWrap)
                .fontWeight(FontWeight.Bold)
                .margin(bottom = 8.px),
            inputModifier = Modifier,
        ) {
            form = form.copy(
                sku = it.value,
            )
            skuNameError = it.value.validateInput(minLength = 3, maxLength = 128)
        }

        GRStringInput(
            direction = FlexDirection.Column,
            inputId = "SkuDescription",
            labelText = "Product Description",
            value = form.description,
            hasError = skuDescriptionError.isNotBlank(),
            labelError = skuDescriptionError,
            labelModifier = Modifier
                .whiteSpace(WhiteSpace.NoWrap)
                .fontWeight(FontWeight.Bold)
                .margin(bottom = 8.px),
            inputModifier = Modifier,
        ) {
            form = form.copy(
                description = it.value,
            )
            skuDescriptionError = it.value.validateInput(minLength = 3, maxLength = 128)
        }

        GRDollarInput(
            direction = FlexDirection.Column,
            inputId = "SkuUnitPrice",
            labelText = "Unit Price ($)",
            value = form.unitPrice,
            hasError = skuUnitPriceError.isNotBlank(),
            labelError = skuUnitPriceError,
            labelModifier = Modifier
                .whiteSpace(WhiteSpace.NoWrap)
                .fontWeight(FontWeight.Bold)
                .margin(bottom = 8.px),
        ) {
            val parsed = parseCurrencyInput(it.value)
            skuUnitPriceError = parsed?.validateCurrencyInput(min = 100) ?: "Not Valid Number"
            if (skuUnitPriceError.isBlank()) {
                form = form.copy(
                    unitPrice = parsed!!
                )
            }
        }

        GRStringInput(
            direction = FlexDirection.Column,
            inputId = "SkuAmountPerReceipt",
            labelText = "Amount per Receipt",
            value = form.amountPerReceipt.toString(),
            hasError = skuAmountPerReceiptError.isNotBlank(),
            labelError = skuAmountPerReceiptError,
            labelModifier = Modifier
                .whiteSpace(WhiteSpace.NoWrap)
                .fontWeight(FontWeight.Bold)
                .margin(bottom = 8.px),
        ) {
            var parsed = it.value.toIntOrNull()
            if (parsed == null || parsed == 0) { parsed = AMOUNT_PER_RECEIPT_DEFAULT }
            skuAmountPerReceiptError = parsed.validateInput(min = 0)
            if (skuAmountPerReceiptError.isBlank()) {
                form = form.copy(
                    amountPerReceipt = parsed
                )
            }
        }

        key(formError) {
            P(GeneralFormErrorTextStyle.toModifier().toAttrs()) {
                Text(formError)
            }
        }

        Button(
            attrs = Modifier
                .disabled(skuAmountPerReceiptError.isNotBlank() || skuNameError.isNotBlank() || skuDescriptionError.isNotBlank() || skuUnitPriceError.isNotBlank())
                .classNames("btn", "btn-primary")
                .margin(top = 16.px)
                .onClick { onSubmit(form) }
                .toAttrs()
        ) {
            Text("Submit")
        }
    }
}